import { useEffect, useState, useCallback } from 'react';

/**
 * Tiny wrapper around the "fetch + cancellable effect + manual refresh"
 * pattern repeated across every admin list page.
 *
 * Usage:
 *
 *   const { data, loading, error, refresh, isRefreshing } = useRefreshableFetch(
 *     () => adminApi.customers.list({ segment, plan }),
 *     [segment, plan],
 *   );
 *
 * `refresh()` re-runs the effect without changing any dep — it just bumps
 * an internal tick. `isRefreshing` is true on background reloads (after
 * the first paint) so the UI can show a small spinner without going back
 * to the empty-state "Loading..." flash.
 */
export function useRefreshableFetch<T>(
  fetcher: () => Promise<T>,
  deps: ReadonlyArray<unknown>,
): {
  data: T | null;
  loading: boolean;
  isRefreshing: boolean;
  error: string | null;
  refresh: () => void;
  setData: (d: T | null) => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    // First load = full loading state. Subsequent reloads = isRefreshing.
    if (data === null) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    setError(null);
    fetcher()
      .then((d) => {
        if (cancelled) return;
        setData(d);
      })
      .catch((e) => {
        if (cancelled) return;
        setError((e as Error).message);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        setIsRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  return { data, loading, isRefreshing, error, refresh, setData };
}
